
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SUBMIT_URL = 'https://readdy.ai/api/form/d6j4rijcs59vsrrrjspg';

const PERKS = [
  { icon: 'ri-video-line', label: 'Weekly viral cat videos' },
  { icon: 'ri-price-tag-3-line', label: 'Exclusive member discounts' },
  { icon: 'ri-heart-line', label: 'Adoption alerts near you' },
  { icon: 'ri-book-open-line', label: 'Free cat care guides' },
];

const AVATARS = [
  'https://public.readdy.ai/ai/img_res/8e4996a559cf2de4ed45251e380e29d6.jpg',
  'https://readdy.ai/api/search-image?query=cute%20cartoon%20avatar%20of%20a%20smiling%20man%20with%20glasses%2C%20simple%20flat%20illustration%20style%2C%20warm%20colors%2C%20friendly%20expression%2C%20circular%20portrait&width=80&height=80&seq=av2&orientation=squarish',
  'https://readdy.ai/api/search-image?query=cute%20cartoon%20avatar%20of%20a%20smiling%20young%20woman%20with%20curly%20hair%2C%20simple%20flat%20illustration%20style%2C%20warm%20colors%2C%20friendly%20expression%2C%20circular%20portrait&width=80&height=80&seq=av3&orientation=squarish',
  'https://readdy.ai/api/search-image?query=cute%20cartoon%20avatar%20of%20a%20smiling%20older%20man%20with%20beard%2C%20simple%20flat%20illustration%20style%2C%20warm%20colors%2C%20friendly%20expression%2C%20circular%20portrait&width=80&height=80&seq=av4&orientation=squarish',
];

export default function MeowNewsletterStrip() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('submitting');
    try {
      const params = new URLSearchParams();
      params.append('email', email);
      params.append('name', name);
      params.append('source', 'newsletter_strip');
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) setStatus('success');
      else setStatus('error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="py-16 px-4 bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 relative overflow-hidden">
      {/* Decorative paws */}
      <div className="absolute inset-0 pointer-events-none select-none opacity-10">
        {['top-6 left-10', 'top-12 right-20', 'bottom-8 left-1/4', 'bottom-4 right-1/3', 'top-1/2 left-6', 'top-1/3 right-8'].map((pos, i) => (
          <span key={i} className={`absolute text-5xl ${pos}`}>🐾</span>
        ))}
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

          {/* Left — Copy */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 text-white text-xs font-bold rounded-full mb-4">
              <i className="ri-mail-line"></i>
              JOIN 12,400+ CAT LOVERS
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
              Join the Meow Squad.<br />
              <span className="text-teal-100">Get the Good Stuff.</span>
            </h2>
            <p className="text-teal-100 text-base mb-6">
              Weekly cat content, exclusive deals, and a free Cat Care Guide — straight to your inbox. No fluff. Just cats.
            </p>

            {/* Perks */}
            <div className="grid grid-cols-2 gap-2.5 mb-6">
              {PERKS.map((perk, i) => (
                <div key={i} className="flex items-center gap-2 text-white/90 text-sm">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20">
                    <i className={`${perk.icon} text-xs`}></i>
                  </div>
                  {perk.label}
                </div>
              ))}
            </div>

            {/* Social proof */}
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {AVATARS.map((src, i) => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-teal-500 overflow-hidden bg-teal-200">
                    <img src={src} alt="Member" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              <div className="text-sm text-teal-100">
                <strong className="text-white">12,400+</strong> members already inside
              </div>
            </div>
          </motion.div>

          {/* Right — Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
              <AnimatePresence mode="wait">
                {status === 'success' ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-6"
                  >
                    <div className="text-5xl mb-3">🎉</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Welcome to the Squad!</h3>
                    <p className="text-gray-500 text-sm">
                      Your <strong>Free Cat Care Guide</strong> is heading to your inbox right now. Check your email!
                    </p>
                  </motion.div>
                ) : (
                  <motion.div key="form">
                    <div className="mb-5">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">Get Your Free Cat Care Guide</h3>
                      <p className="text-sm text-gray-500">Enter your details below — it's completely free.</p>
                    </div>
                    <form data-readdy-form onSubmit={handleSubmit} className="space-y-3">
                      <div>
                        <input
                          type="text"
                          name="name"
                          value={name}
                          onChange={e => setName(e.target.value)}
                          placeholder="First name"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          required
                          placeholder="Email address"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                        />
                      </div>
                      {status === 'error' && (
                        <p className="text-rose-500 text-xs">Something went wrong. Please try again.</p>
                      )}
                      <button
                        type="submit"
                        disabled={status === 'submitting'}
                        className="w-full py-3.5 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-200 text-white font-bold rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-lg shadow-teal-100"
                      >
                        {status === 'submitting' ? (
                          <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                        ) : (
                          <><i className="ri-gift-line"></i> Get My Free Guide + Join the Squad</>
                        )}
                      </button>
                    </form>
                    <div className="flex items-center justify-center gap-4 mt-3">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-lock-line text-teal-400"></i> No spam
                      </span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-close-circle-line text-teal-400"></i> Unsubscribe anytime
                      </span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-shield-check-line text-teal-400"></i> 100% free
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
